import React, { useState } from 'react';

import { link, routes } from '@aline/routes';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { Helmet } from 'react-helmet';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { InsightsPage } from 'src/components/insights';
import { InsightChartBuilderPage } from 'src/components/insights/InsightChartBuilderPage';
import { createRelativeRouter } from 'src/helpers/router-helpers';
import type { ReactSetStateTuple } from 'src/types';
import { getIsPlainRecord } from 'src/utils/get-is-plain-record';
import { makeStyles } from 'tss-react/mui';

import { Board, initialBoardState } from './board/Board';
import type { BoardLocalState } from './board/Board';
import { BoardSettings } from './board/BoardSettings';
import type { WorkflowListChartsIntegrationSlice } from './workflow-list/workflow-list-charts';
import {
  createDefaultWorkflowListChartConfig,
  WORKFLOW_LIST_CHART_ID,
} from './workflow-list/workflow-list-charts';
import type { WorkflowListPresetId } from './workflow-list/workflow-views';
import { DEFAULT_WORKFLOW_LIST_PRESET } from './workflow-list/workflow-views';
import { WorkflowsList } from './workflow-list/WorkflowsList';
import { WorkflowsToolbar } from './workflow-list/WorkflowsToolbar';

const useStyles = makeStyles()(() => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minWidth: 0,
    position: 'relative',
    width: '100%',
  },
  routeContent: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    minWidth: 0,
  },
}));

const { toRelativePath: toRelativeWorkflowsPath } = createRelativeRouter(
  routes.WorkflowIndexMustRedirect,
  { nestedRoutes: true }
);

export interface WorkflowsLayoutContext {
  activeWorkflowViewId: string | null;
  boardStateTuple: ReactSetStateTuple<BoardLocalState>;
  chartSearch: string;
  chartsIntegrationSlice: WorkflowListChartsIntegrationSlice;
  filtersAnchorEl: HTMLElement | null;
  onApiRefChange: (apiRef: React.RefObject<GridApiPremium | null> | null) => void;
  onBoardChange: (boardId: string) => void;
  onChartsIntegrationSliceChange: (slice: WorkflowListChartsIntegrationSlice) => void;
  selectedBoardId: string | undefined;
  workflowListPreset: WorkflowListPresetId;
}

const INITIAL_CHARTS_INTEGRATION_SLICE: WorkflowListChartsIntegrationSlice = {
  activeChartId: WORKFLOW_LIST_CHART_ID,
  charts: { [WORKFLOW_LIST_CHART_ID]: createDefaultWorkflowListChartConfig() },
};

function getSelectedBoardIdFromLocationState(state: unknown) {
  if (!getIsPlainRecord(state)) return undefined;
  return typeof state.selectedBoardId === 'string' ? state.selectedBoardId : undefined;
}

function WorkflowsShell() {
  const location = useLocation();
  const { classes } = useStyles();
  const boardStateTuple = useState(initialBoardState);
  const chartSearchTuple = useState('');
  const [workflowListPreset, setWorkflowListPreset] = useState<WorkflowListPresetId>(
    DEFAULT_WORKFLOW_LIST_PRESET
  );
  const [activeWorkflowViewId, setActiveWorkflowViewId] = useState<string | null>(null);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<HTMLElement | null>(null);
  const [workflowListApiRef, setWorkflowListApiRef] =
    useState<React.RefObject<GridApiPremium | null> | null>(null);
  const [chartsIntegrationSlice, setChartsIntegrationSlice] =
    useState<WorkflowListChartsIntegrationSlice>(INITIAL_CHARTS_INTEGRATION_SLICE);
  const [selectedBoardId, setSelectedBoardId] = useState<string | undefined>(
    getSelectedBoardIdFromLocationState(location.state)
  );
  const outletContext: WorkflowsLayoutContext = {
    activeWorkflowViewId,
    boardStateTuple,
    chartSearch: chartSearchTuple[0],
    chartsIntegrationSlice,
    filtersAnchorEl,
    onApiRefChange: setWorkflowListApiRef,
    onBoardChange: setSelectedBoardId,
    onChartsIntegrationSliceChange: setChartsIntegrationSlice,
    selectedBoardId,
    workflowListPreset,
  };

  return (
    <ContentWrapper className={classes.page}>
      <WorkflowsToolbar
        activeWorkflowViewId={activeWorkflowViewId}
        boardStateTuple={boardStateTuple}
        chartSearchTuple={chartSearchTuple}
        chartsIntegrationSlice={chartsIntegrationSlice}
        filtersButtonRef={setFiltersAnchorEl}
        listApiRef={workflowListApiRef}
        selectedBoardId={selectedBoardId}
        workflowListPreset={workflowListPreset}
        onActiveWorkflowViewIdChange={setActiveWorkflowViewId}
        onBoardChange={setSelectedBoardId}
        onChartsIntegrationSliceChange={setChartsIntegrationSlice}
        onWorkflowListPresetChange={setWorkflowListPreset}
      />
      <div className={classes.routeContent}>
        <Outlet context={outletContext} />
      </div>
    </ContentWrapper>
  );
}

function WorkflowsListBody() {
  const {
    activeWorkflowViewId,
    chartsIntegrationSlice,
    filtersAnchorEl,
    onApiRefChange,
    onChartsIntegrationSliceChange,
    workflowListPreset,
  } = useOutletContext<WorkflowsLayoutContext>();

  return (
    <WorkflowsList
      activeWorkflowViewId={activeWorkflowViewId}
      chartsIntegrationSlice={chartsIntegrationSlice}
      filtersAnchorEl={filtersAnchorEl}
      workflowListPreset={workflowListPreset}
      onApiRefChange={onApiRefChange}
      onChartsIntegrationSliceChange={onChartsIntegrationSliceChange}
    />
  );
}

function InsightsBodyRoute() {
  const { chartSearch } = useOutletContext<WorkflowsLayoutContext>();

  return <InsightsPage search={chartSearch} />;
}

function BoardBodyRoute() {
  const { boardStateTuple, onBoardChange, selectedBoardId } =
    useOutletContext<WorkflowsLayoutContext>();

  return (
    <Board
      boardStateTuple={boardStateTuple}
      selectedBoardId={selectedBoardId}
      onBoardChange={onBoardChange}
    />
  );
}

/**
 * The workflow detail page lives at `/workflow/:workflowId` (routed off `MainPage`), but
 * emails, Slack messages and CRM records sent before the move still point at
 * `/workflows/:workflowId/...`. Send those to the current URL, tab and all.
 */
function LegacyWorkflowDetailRedirect() {
  const { hash, search } = useLocation();
  const { '*': tabPath, workflowId } = useParams();

  if (!workflowId) return <Navigate replace to={link.WorkflowList()} />;

  const tabSuffix = tabPath ? `/${tabPath}` : '';
  const detailPath = link.WorkflowDetail({ workflowId });

  return <Navigate replace to={`${detailPath}${tabSuffix}${search}${hash}`} />;
}

/**
 * The list, board and board-settings views sit inside a padded `ContentWrapper`; the
 * detail page does not, which is why it is routed off `MainPage` rather than from here.
 */
export function WorkflowsPage() {
  const { classes } = useStyles();

  return (
    <>
      <Helmet>
        <title>Workflows</title>
      </Helmet>
      <Routes>
        <Route
          element={
            <ContentWrapper className={classes.page}>
              <InsightChartBuilderPage />
            </ContentWrapper>
          }
          path={toRelativeWorkflowsPath(routes.WorkflowInsightBuilder)}
        />
        <Route element={<WorkflowsShell />}>
          <Route
            element={<WorkflowsListBody />}
            path={toRelativeWorkflowsPath(routes.WorkflowList)}
          />
          <Route
            element={<BoardBodyRoute />}
            path={toRelativeWorkflowsPath(routes.WorkflowBoard)}
          />
          <Route
            element={<InsightsBodyRoute />}
            path={toRelativeWorkflowsPath(routes.WorkflowInsights)}
          />
        </Route>
        <Route
          element={<Navigate replace to={link.WorkflowList()} />}
          path={toRelativeWorkflowsPath(routes.WorkflowAll)}
        />
        <Route
          element={<Navigate replace to={link.WorkflowList()} />}
          path={toRelativeWorkflowsPath(routes.WorkflowCompleted)}
        />
        <Route
          element={
            <ContentWrapper className={classes.page}>
              <BoardSettings />
            </ContentWrapper>
          }
          path={toRelativeWorkflowsPath(routes.WorkflowBoardSettings)}
        />
        <Route element={<LegacyWorkflowDetailRedirect />} path="/:workflowId/*" />
        <Route element={<Navigate replace to={link.WorkflowList()} />} path="/" />
      </Routes>
    </>
  );
}
